import {
	Alert,
	Box,
	Checkbox,
	Divider,
	FormControlLabel,
	Grid,
	TextField,
	TextareaAutosize,
	Typography,
} from "@mui/material";
import React from "react";
import styles from "./Invoice.module.scss";
import { styled } from "@mui/system";
import { formatDate, formatTime } from "../../../../../utils/dateUtils";
import { DateTimePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { Controller } from "react-hook-form";

const blue = {
	100: "#DAECFF",
	200: "#b6daff",
	400: "#3399FF",
	500: "#007FFF",
	600: "#0072E5",
	900: "#003A75",
};

const grey = {
	50: "#f6f8fa",
	100: "#eaeef2",
	200: "#d0d7de",
	300: "#afb8c1",
	400: "#8c959f",
	500: "#6e7781",
	600: "#57606a",
	700: "#424a53",
	800: "#32383f",
	900: "#24292f",
};

const StyledTextarea = styled(TextareaAutosize)(
	({ theme }) => `
    width: 100%;
    font-family: IBM Plex Sans, sans-serif;
    font-size: 0.875rem;
    font-weight: 400;
    line-height: 1.5;
    padding: 2px;
		padding-bottom: "2px",
    color: ${theme.palette.mode === "dark" ? grey[300] : grey[900]};
    background: ${theme.palette.mode === "dark" ? grey[900] : "#fff"};
    border: 1px solid ${theme.palette.mode === "dark" ? grey[700] : grey[200]};
    box-shadow: 0px 2px 2px ${theme.palette.mode === "dark" ? grey[900] : grey[50]};
  
    &:hover {
      border-color: ${blue[400]};
    }
  
    &:focus {
      border-color: ${blue[400]};
      box-shadow: 0 0 0 3px ${theme.palette.mode === "dark" ? blue[500] : blue[200]};
    }
  
    // firefox
    &:focus-visible {
      outline: 0;
    }
  `
);

const Invoices = ({ register, setValue, watch, load, control, errors, index, handleUseForAll }) => {
	return (
		<div className={styles.pdfArea} style={{ marginBottom: "30px" }}>
			<Box sx={{ marginBottom: "5px" }} display="flex" alignItems="center" gap={1}>
				<LocalizationProvider dateAdapter={AdapterDayjs}>
					<Controller
						name={`lastDate_${index}`}
						control={control}
						defaultValue={null}
						rules={{ required: "Overdue date is required!" }}
						render={({ field }) => (
							<>
								<DateTimePicker
									{...field}
									label="Overdue date"
									slotProps={{ textField: { size: "small" } }}
									inputFormat="MM/dd/yyyy"
									value={field.value || null}
									onChange={(newValue) => {
										field.onChange(newValue);
									}}
									renderInput={(params) => (
										<TextField
											{...params}
											size="small"
											error={Boolean(errors.lastDate)}
											helperText={errors.lastDate?.message || ""}
										/>
									)}
								/>
								{field.value && (
									<FormControlLabel
										control={
											<Checkbox
												onChange={(e) => {
													handleUseForAll(e.target.checked, index);
												}}
											/>
										}
										label="Use For All"
									/>
								)}
							</>
						)}
					/>
				</LocalizationProvider>
				{Boolean(errors[`lastDate_${index}`]) && (
					<Alert variant="outlined" severity="error">
						{errors[`lastDate_${index}`]?.message || ""}
					</Alert>
				)}
			</Box>

			<div className="pdfMain">
				<Grid container spacing={2}>
					<Grid item sm={6}>
						<div className="logo mb-3">
							{/* <img src={`/${APP}_Logo.png`} style={{ width: "130px", height: "auto" }} alt="Logo" /> */}
							<Typography sx={{ fontWeight: "bold", marginBottom: "15px", fontSize: "20px" }}>
								{watch(`orgName_${index}`)}
							</Typography>
						</div>
						<TextField
							{...register(`address_1_${index}`)}
							id="small-input"
							variant="outlined"
							value={watch(`address_1_${index}`)}
							size="small"
							className={styles.customInput}
							placeholder="address 1"
						/>
						<TextField
							{...register(`address_2_${index}`)}
							id="small-input"
							variant="outlined"
							value={watch(`address_2_${index}`)}
							size="small"
							className={styles.customInput}
							placeholder="address 2"
						/>

						<div className={styles.inlineLabelInput}>
							<label>
								<b>Phone: </b>
							</label>
							<TextField
								{...register(`phone_${index}`)}
								id="small-input"
								variant="outlined"
								value={watch(`phone_${index}`)}
								size="small"
								className={styles.customInput}
							/>
						</div>
					</Grid>
					<Grid item sm={6}>
						<p>
							<b>INVOICE</b>
							<div className={styles.inlineLabelInput}>
								<label>
									<b>Invoice # </b>
								</label>
								<TextField
									{...register(`loadId_${index}`)}
									id="small-input"
									value={watch(`loadId_${index}`)}
									variant="outlined"
									size="small"
									className={styles.customInput}
								/>
							</div>

							<div className={styles.inlineLabelInput}>
								<label>
									<b>Date </b>
								</label>
								<TextField
									{...register(`load_date_${index}`)}
									id="small-input"
									value={watch(`load_date_${index}`)}
									variant="outlined"
									size="small"
									className={styles.customInput}
								/>
							</div>

							<div className={styles.inlineLabelInput}>
								<label>
									<b>Reference </b>
								</label>
								<TextField
									{...register(`load_reference_${index}`)}
									id="small-input"
									value={watch(`load_reference_${index}`)}
									variant="outlined"
									size="small"
									className={styles.customInput}
								/>
							</div>

							<div className={styles.inlineLabelInput}>
								<label>
									<b>Weight </b>
								</label>
								<TextField
									{...register(`weight_${index}`)}
									id="small-input"
									value={
										watch(`weight_${index}`) === "NaN" || watch(`weight_${index}`) === "NaN lbs"
											? "0 lbs"
											: watch(`weight_${index}`)
									}
									variant="outlined"
									size="small"
									className={styles.customInput}
								/>
							</div>
						</p>
					</Grid>
				</Grid>
				<div className="customerInformation mt-4">
					<p>
						<b>Customer Information</b>
					</p>
					<Divider />
					<Grid style={{ marginTop: "2px" }} container columnSpacing={2} rowSpacing={1}>
						<Grid item sm={6}>
							<TextField
								{...register(`company_name_${index}`)}
								id="small-input"
								variant="outlined"
								value={watch(`company_name_${index}`)}
								size="small"
								className={styles.customInput}
								placeholder="Company Name"
							/>
							<TextField
								{...register(`company_address_${index}`)}
								id="small-input"
								variant="outlined"
								value={watch(`company_address_${index}`)}
								size="small"
								className={styles.customInput}
								placeholder="Company Address"
							/>
							<TextField
								{...register(`company_phone_${index}`)}
								id="small-input"
								variant="outlined"
								value={watch(`company_phone_${index}`)}
								size="small"
								className={styles.customInput}
								placeholder="Company Phone"
							/>
						</Grid>
						<Grid item sm={6}>
							<div className={styles.inlineLabelInput}>
								<label style={{ minWidth: 136 }}>
									<b>Primary Contact </b>
								</label>
								<TextField
									{...register(`billing_primary_contact_${index}`)}
									id="small-input"
									value={watch(`billing_primary_contact_${index}`)}
									variant="outlined"
									size="small"
									className={styles.customInput}
								/>
							</div>
							<div className={styles.inlineLabelInput}>
								<label style={{ minWidth: 136 }}>
									<b>Phone </b>
								</label>
								<TextField
									{...register(`billing_primary_phone_${index}`)}
									id="small-input"
									value={watch(`billing_primary_phone_${index}`)}
									variant="outlined"
									size="small"
									className={styles.customInput}
								/>
							</div>
							<div className={styles.inlineLabelInput}>
								<label style={{ minWidth: 136 }}>
									<b>Fax </b>
								</label>
								<TextField
									{...register(`billing_primary_fax_${index}`)}
									id="small-input"
									variant="outlined"
									value={watch(`billing_primary_fax_${index}`)}
									size="small"
									className={styles.customInput}
								/>
							</div>
						</Grid>
					</Grid>
				</div>
				<div className="stopActions" style={{ marginTop: "8px" }}>
					<b>Pay Items</b>
					<Divider />
					<table className={styles.table}>
						<thead>
							<tr>
								<th className={styles.header}> Description </th>
								<th className={styles.header}> Notes </th>
								<th className={styles.header}> Quantity </th>
								<th className={styles.header}> Rate </th>
								<th className={styles.header}> Amount </th>
							</tr>
						</thead>
						<tbody>
							<tr className={styles.row}>
								<td className={styles.cell}>
									{" "}
									<TextField
										{...register(`paytm_description_${index}`)}
										id="small-input"
										value={watch(`paytm_description_${index}`)}
										variant="outlined"
										size="small"
										className={styles.customInput}
									/>{" "}
								</td>
								<td className={styles.cell}>
									{" "}
									<StyledTextarea
										{...register(`paytm_notes_${index}`)}
										minRows={1}
										value={watch(`paytm_notes_${index}`)}
										aria-label="empty textarea"
									/>{" "}
								</td>
								<td className={styles.cell}>
									<div className={styles.inlineLabelInput}>
										<TextField
											{...register(`paytm_quantity_${index}`)}
											id="small-input"
											variant="outlined"
											size="small"
											className={styles.customInput}
											type="number"
											onChange={(e) => {
												setValue(
													`paytm_amount_${index}`,
													Number(e.currentTarget.value) * Number(watch(`paytm_rate_${index}`))
												);
											}}
										/>
									</div>
								</td>
								<td className={styles.cell}>
									{" "}
									<TextField
										{...register(`paytm_rate_${index}`)}
										id="small-input"
										variant="outlined"
										size="small"
										className={styles.customInput}
									/>{" "}
								</td>
								<td className={styles.cell}>
									{" "}
									<TextField
										{...register(`paytm_amount_${index}`)}
										id="small-input"
										variant="outlined"
										size="small"
										className={styles.customInput}
									/>{" "}
								</td>
							</tr>
						</tbody>
					</table>
				</div>
				<div className="stopActions" style={{ marginTop: "14px" }}>
					<h5>Stops / Actions</h5>
					<Divider />
					<table className={styles.table}>
						<thead>
							<tr>
								<th className={styles.header}> # </th>
								<th className={styles.header}> Action </th>
								<th className={styles.header}> Date/Time </th>
								<th className={styles.header}> Location </th>
								<th className={styles.header}> Contact </th>
							</tr>
						</thead>
						<tbody>
							<tr className={styles.row}>
								<td rowSpan="2" className={styles.cell}>
									{" "}
									1{" "}
								</td>
								<td className={styles.cell}> Pickup </td>
								<td className={styles.cell}>
									{load.pickUpList &&
										load.pickUpList.length &&
										load.pickUpList.map((pickup, ind) => (
											<>
												{" "}
												<TextField
													{...register(`p_date_${index}_${ind}`)}
													id="small-input"
													variant="outlined"
													value={watch(`p_date_${index}_${ind}`)}
													size="small"
													className={styles.customInput}
													placeholder="Date/Time"
													defaultValue={`${formatDate(pickup.pickUpOpeningDateTime)} ${formatTime(
														pickup.pickUpOpeningDateTime
													)}`}
												/>{" "}
											</>
										))}{" "}
								</td>
								<td className={styles.cell}>
									{load.pickUpList &&
										load.pickUpList.length &&
										load.pickUpList.map((pickup, ind) => (
											<>
												{" "}
												<TextField
													{...register(`p_location_${index}_${ind}`)}
													id="small-input"
													variant="outlined"
													value={watch(`p_location_${index}_${ind}`)}
													size="small"
													className={styles.customInput}
													placeholder="Location"
													defaultValue={pickup.pickUpLocation}
												/>{" "}
											</>
										))}
								</td>
								<td className={styles.cell}>
									{load.pickUpList &&
										load.pickUpList.length &&
										load.pickUpList.map((pickup, ind) => (
											<div key={pickup._id} className={styles.inlineLabelInput}>
												<label>
													<b>Phone: </b>
												</label>
												<TextField
													{...register(`p_phone_${index}_${ind}`)}
													value={watch(`p_phone_${index}_${ind}`)}
													id="small-input"
													variant="outlined"
													size="small"
													className={styles.customInput}
													defaultValue=""
												/>
											</div>
										))}
								</td>
							</tr>
							<tr className={styles.row}>
								<td colSpan="4" className={`${styles.cell} ${styles.referenced}`}>
									<div className={styles.inlineLabelInput}>
										<label>
											<b>References: </b>
										</label>
										<TextField
											{...register(`p_references_${index}`)}
											id="small-input"
											value={watch(`p_references_${index}`)}
											variant="outlined"
											size="small"
											className={styles.customInput}
										/>
									</div>
								</td>
							</tr>
							<tr className={styles.row}>
								<td className={styles.cell}> 2 </td>
								<td className={styles.cell}> Delivery </td>
								<td className={styles.cell}>
									{load.destinationList &&
										load.destinationList.length &&
										load.destinationList.map((des, ind) => (
											<>
												{" "}
												<TextField
													{...register(`d_date_${index}_${ind}`)}
													id="small-input"
													variant="outlined"
													value={watch(`d_date_${index}_${ind}`)}
													size="small"
													className={styles.customInput}
													placeholder="Date/Time"
													defaultValue={`${formatDate(des.dropOpeningDateTime)} ${formatTime(
														des.dropOpeningDateTime
													)}`}
												/>{" "}
											</>
										))}
								</td>
								<td className={styles.cell}>
									{load.destinationList &&
										load.destinationList.length &&
										load.destinationList.map((des, ind) => (
											<>
												{" "}
												<TextField
													{...register(`d_location_${index}_${ind}`)}
													id="small-input"
													variant="outlined"
													value={watch(`d_location_${index}_${ind}`)}
													size="small"
													className={styles.customInput}
													placeholder="Location"
													defaultValue={des.destination}
												/>{" "}
											</>
										))}
								</td>
								<td className={styles.cell}>
									{load.destinationList &&
										load.destinationList.length &&
										load.destinationList.map((des, ind) => (
											<div key={des._id} className={styles.inlineLabelInput}>
												<label>
													<b>Phone: </b>
												</label>
												<TextField
													{...register(`d_phone_${index}_${ind}`)}
													id="small-input"
													variant="outlined"
													value={watch(`d_phone_${index}_${ind}`)}
													size="small"
													className={styles.customInput}
													defaultValue=""
												/>
											</div>
										))}
								</td>
							</tr>
						</tbody>
					</table>
				</div>

				<div className="account Information">
					<p>
						<b>Accounting Information</b>
					</p>
					<Divider />
					<Grid style={{ marginTop: "2px" }} container columnSpacing={2} rowSpacing={1}>
						<Grid item sm={12}>
							<TextField
								{...register(`accountType_${index}`)}
								id="small-input"
								variant="outlined"
								size="small"
								className={styles.customInput}
								placeholder="Account Type"
							/>
							<TextField
								{...register(`depositType_${index}`)}
								id="small-input"
								variant="outlined"
								size="small"
								className={styles.customInput}
								placeholder="Deposit Type"
							/>
							<TextField
								{...register(`abaRoutingNumber_${index}`)}
								id="small-input"
								variant="outlined"
								size="small"
								className={styles.customInput}
								placeholder="ABA Routing Number"
							/>
							<TextField
								{...register(`accountNumber_${index}`)}
								id="small-input"
								variant="outlined"
								size="small"
								className={styles.customInput}
								placeholder="Accaount Number"
							/>
						</Grid>
					</Grid>
				</div>
			</div>
			{Boolean(errors[`lastDate_${index}`]) && (
				<Alert
					sx={{ textAlign: "right", float: "right", width: "320px", marginTop: "10px" }}
					variant="outlined"
					severity="error"
				>
					{errors[`lastDate_${index}`]?.message || ""}
				</Alert>
			)}
		</div>
	);
};

export default Invoices;
